import { is } from 'ramda';
import { Widget } from './widget';
const EMPTY = null;
function create(rows, cols, value) {
    if (!Number.isNaN(rows) && rows > 0) {
        const matrix = Array(rows);
        for (let i = 0; i < rows; i++) {
            matrix[i] = Array(cols);
            for (let j = 0; j < cols; j++) {
                matrix[i][j] = value;
            }
        }
        return matrix;
    }
    else {
        throw new Error('rows must be a positive number');
    }
}
export class Matrix {
    constructor(rows = 100, cols = rows, data = EMPTY, force = false) {
        this.value = create(rows, cols, data);
        this.widgets = [];
        this.force = force;
    }
    get() {
        return this.value;
    }
    getItem(row, col) {
        return this.value[row][col];
    }
    get width() {
        return this.value[0].length;
    }
    get height() {
        return this.value.length;
    }
    set(row, col, value) {
        this.value[row][col] = value;
    }
    fill(content) {
        content.forEach((row, rowId) => {
            row.forEach((item, columnId) => {
                this.value[rowId][columnId] = item;
            });
        });
        return this;
    }
    createNewLines(width, count) {
        return create(count, width, null);
    }
    addLines(count, matrix = this.value) {
        return matrix.concat(this.createNewLines(matrix[0].length, count));
    }
    hasWidowLine(matrix, y, x, height) {
        var _a;
        return x > 0 && ((_a = matrix[y + height - 1]) === null || _a === void 0 ? void 0 : _a[x - 1]) === (undefined || EMPTY);
    }
    isInWidget(widget, { row, column, startY, startX }) {
        const { height, width } = widget;
        return (row - startY >= 0 && row - startY < height && column - startX >= 0 && column - startX < width);
    }
    _addWidget(widget, matrix) {
        if (this.force) {
            widget.x = undefined;
            widget.y = undefined;
        }
        this.widgets.push(widget);
        const { startX, startY, matrix: updatedMatrix } = this.getFreePlace(widget, matrix);
        updatedMatrix.forEach((row, rowId) => {
            row.forEach((column, columnId) => {
                const value = this.isInWidget(widget, {
                    row: rowId,
                    column: columnId,
                    startY: is(Number, widget.y) ? Number(widget.y) : startY,
                    startX: is(Number, widget.x) ? Number(widget.x) : startX
                })
                    ? widget.value
                    : updatedMatrix[rowId][columnId];
                updatedMatrix[rowId][columnId] = value;
            });
        });
        return updatedMatrix;
    }
    _removeWidget(widget, matrix) {
        const { width, height, x = 0, y = 0 } = widget;
        const widgetFromStore = this.widgets.findIndex((w) => w.value === widget.value);
        this.widgets.splice(widgetFromStore, 1);
        matrix.slice(y, y + height).forEach((row, rowId) => {
            row.slice(x, x + width).forEach((cell, columnId) => {
                matrix[y + rowId][x + columnId] = EMPTY;
            });
        });
        return matrix;
    }
    swapWidgetsX(widgetBase, widgetNew, matrix) {
        matrix.forEach((row, rowId) => {
            row.forEach((column, columnId) => {
                const { x: x1 = 0, y: y1 = 0, width: w1, value: v1 } = widgetBase;
                const { x: x2 = 0, y: y2 = 0, width: w2, value: v2 } = widgetNew;
                const value1 = this.isInWidget(widgetNew, {
                    row: rowId,
                    column: columnId,
                    startY: y1,
                    startX: x1
                })
                    ? v2
                    : matrix[rowId][columnId];
                matrix[rowId][columnId] = value1;
                const value2 = this.isInWidget(widgetBase, {
                    row: rowId,
                    column: columnId,
                    startY: y2,
                    startX: w2 + x2 - w1
                })
                    ? v1
                    : matrix[rowId][columnId];
                matrix[rowId][columnId] = value2;
            });
        });
        return matrix;
    }
    moveWidget(widget, matrix, coordinates) {
        const updatedWidget = { ...widget, ...coordinates };
        const matrixAfterRemove = this._removeWidget(widget, matrix);
        return this._addWidget(updatedWidget, matrixAfterRemove);
    }
    getFreePlace(widget, matrix) {
        const { x, y } = widget;
        if (typeof x !== 'undefined' && typeof y !== 'undefined' && this.isPlaceFree(widget, matrix)) {
            return this.validateCoordinates(widget, matrix);
        }
        else {
            const coord = this.findFreePlace(widget, matrix);
            return this.validateCoordinates({ ...widget, x: coord.startX, y: coord.startY }, matrix);
        }
    }
    validateCoordinates(widget, matrix) {
        // avoid undefined values but without change coordianates to valid value
        const { x = -1, y = -1 } = widget;
        const needLines = this.countNeededLines(widget, matrix);
        if (needLines > 0) {
            const updatedMatrix = this.addLines(needLines, matrix);
            return { ...this.findFreePlace(widget, updatedMatrix), matrix: updatedMatrix };
        }
        if (x >= 0 && y >= 0)
            return { startX: x, startY: y, matrix };
        return { startX: widget.x || 0, startY: widget.y || 0, matrix };
    }
    isPlaceFree(widget, matrix) {
        const { width, height, x = 0, y = 0 } = widget;
        return matrix.slice(y, y + height).every((line) => {
            return line.slice(x, x + width).every(this.isEmpty);
        });
    }
    getNewLines(widget, matrix) {
        const updatedMatrix = this.addLines(widget.height, matrix);
        return { startY: matrix.length, startX: 0, updatedMatrix };
    }
    countNeededLines(widget, matrix) {
        const { height, y } = widget;
        if (typeof y === 'undefined')
            throw new Error('Line number must be defined');
        const startY = y >= 0 ? y : matrix.length;
        return startY + height - matrix.length;
    }
    findFreePlace(widget, matrix) {
        const { width, height } = widget;
        const coordinates = { startX: -1, startY: -1 };
        const startY = matrix.findIndex((line, row) => {
            const emptys = line.filter(this.isEmpty);
            if (emptys.length && emptys.length >= width) {
                const slots = this.getHolesInLine(line);
                const freeSlot = slots.findIndex((slot, column) => {
                    return (slot.length > 0 &&
                        this.isPlaceFree({ width, height, x: slot.start, y: row }, matrix) &&
                        !this.hasWidowLine(matrix, row, slot.start, height));
                });
                if (freeSlot > -1) {
                    const startX = slots[freeSlot].start;
                    coordinates.startX = startX;
                    return true;
                }
                else {
                    return false;
                }
            }
            return false;
        });
        if (startY > -1) {
            coordinates.startY = startY;
        }
        return coordinates;
    }
    getHolesInLine(line) {
        const slots = [];
        line.forEach((item, i, arr) => {
            if (item === EMPTY && arr[i - 1] !== EMPTY) {
                slots.push({ start: i, length: 1 });
            }
            if (item === EMPTY && arr[i - 1] === EMPTY) {
                slots[slots.length - 1].length += 1;
            }
        });
        return slots;
    }
    isEmpty(item) {
        return item === EMPTY;
    }
    isNotEmpty(item) {
        return item !== EMPTY;
    }
    addWidget(widget) {
        this.value = this._addWidget(widget, this.value);
    }
    addWidgets(widgets, matrix) {
        const _matrix = matrix || this.value.slice();
        const matrixWithWidgets = widgets.reduce((m, widget) => {
            return this._addWidget(widget, m);
        }, _matrix);
        if (typeof matrix === 'undefined') {
            this.value = matrixWithWidgets;
        }
        // this.secondPass(widgets, matrixWithWidgets);
        return matrixWithWidgets;
    }
    getWidgets(matrix = this.value) {
        const widgetsIndex = new Map();
        matrix.forEach((row, rowId) => {
            row.forEach((item, colId) => {
                if (item) {
                    if (widgetsIndex.has(item)) {
                        const currentWidget = widgetsIndex.get(item);
                        currentWidget === null || currentWidget === void 0 ? void 0 : currentWidget.addPoint(colId, rowId);
                    }
                    else {
                        const widget = this.widgets.find((w) => w.value === item);
                        widgetsIndex.set(item, new Widget(colId, rowId, item, widget === null || widget === void 0 ? void 0 : widget.minW, widget === null || widget === void 0 ? void 0 : widget.minH));
                    }
                }
            });
        });
        return Array.from(widgetsIndex.values())
            .filter((value) => !!value)
            .map((widget) => widget.get());
    }
    lineIsEmpty(line) {
        return line.every((item) => item === EMPTY);
    }
    cutEmptyLines(matrix = this.value) {
        const lastLine = matrix[matrix.length - 1];
        if (this.lineIsEmpty(lastLine)) {
            matrix.length = matrix.length - 1;
            this.cutEmptyLines(matrix);
        }
        return matrix;
    }
    resizeMatrix(width, matrix = this.value) {
        const oldWidth = matrix[0].length;
        const multiplier = width / oldWidth;
        if (multiplier === 1)
            return matrix;
        const resizedMatrix = create(this.height, width, EMPTY);
        const widgets = matrix ? this.getWidgets(matrix) : this.getWidgets();
        const resizedWidgets = widgets.map((widget) => this.resizeWidget(widget, multiplier, width, matrix));
        return this.addWidgets(resizedWidgets, resizedMatrix);
    }
    resizeWidget(widget, multiplier, matrixWidth, matrix) {
        let width;
        let height;
        let x;
        let y;
        if (multiplier < 0.5) {
            width = matrixWidth;
            height = widget.height === 1 ? 1 : Math.max(1, Math.floor(widget.height * multiplier));
            x = 0;
        }
        else {
            width = Math.floor(widget.width * multiplier);
            height =
                widget.height === 1 || widget.height * multiplier < 1
                    ? 1
                    : Math.floor(widget.height * multiplier);
            x = widget.x && Math.floor(widget.x * multiplier);
            y = widget.y && this.resizeWidgetHeight(widget, multiplier, matrix);
        }
        return {
            ...widget,
            width,
            height,
            x,
            y
        };
    }
    resizeWidgetHeight(widget, multiplier, oldMatrix) {
        const { x = 0, y = 0 } = widget;
        if (y === 0)
            return 0;
        const widgets = oldMatrix ? this.getWidgets(oldMatrix) : this.getWidgets();
        const upperWidgetsPoints = oldMatrix.slice(0, y).map((line) => line[x]);
        const upperWidgets = new Map();
        let height = 0;
        upperWidgetsPoints.forEach((point) => {
            const currentWidget = widgets.find((w) => w.value === point);
            if (currentWidget)
                upperWidgets.set(currentWidget === null || currentWidget === void 0 ? void 0 : currentWidget.value, currentWidget === null || currentWidget === void 0 ? void 0 : currentWidget.height);
        });
        upperWidgets.delete(EMPTY);
        upperWidgets.forEach((value) => {
            height += value === 1 ? 1 : Math.floor(value * multiplier);
        });
        return height;
    }
    toInternalFormat(value, width, height, minH, minW, x, y) {
        return { value, width, height, minH, minW, x, y };
    }
}
